import { useState, useEffect } from 'react';
import { BatteryHorizIcon, MeterIcon, PcsIcon, PvIcon, IconLabel, ElecLine2, opModeToFlow, opModeToSwitch, LoadIcon } from '../service/SvgIcons';

export function SingleLineSvgOrion(props) {
    const ix0 = 120+20;  // Meter 위치 
    const ix1 = 580+40;  // PCS 위치
    const ix2 = 820+40;  // Bat 위치
    const iy0 = 50;
    const iy1 = 220;
    const iy2 = 385;
    const iw = 75;  // icon width
    const ih = 75;  // icon height
    const jx = 450;  // joint x
    const fs = 24;  // font size

    const [pcs1Obj, setPcs1Obj] = useState(null);
    const [pcs2Obj, setPcs2Obj] = useState(null);
    const [loadObj, setLoadObj] = useState(null);
    const [pvObj, setPvObj] = useState(null);
    const [batObj, setBatObj] = useState(null);
    const [meterObj, setMeterObj] = useState(null);

    const threshold = 1; // 1kW 미만은 0으로 간주

    useEffect(() => {
        if (!props?.objmap) return;
        // Map 객체인지 안전하게 확인 후 get 호출
        if (typeof props.objmap.get === 'function') {
            setPcs1Obj(props.objmap.get('pcs1/1/pcs') || null);
            setPcs2Obj(props.objmap.get('pcs2/1/pcs') || null);
            setPvObj(props.objmap.get('pv/1/inverter') || null);
            setBatObj(props.objmap.get('bms/1/bms') || null);
            setMeterObj(props.objmap.get('meter/1/meter/ch1') || null);
            setLoadObj(props.objmap.get('meter/1/meter/ch2') || null);
        }
    }, [props.objmap]);

    // 헬퍼 함수: 에러 방지용 안전한 값 추출기
    // obj가 Map이든 일반 Object든 안전하게 값을 가져옵니다.
    const getSafeVal = (obj, key) => {
        if (!obj) return undefined;
        if (typeof obj.get === 'function') {
            return obj.get(key)?.val;
        }
        return obj[key]?.val;
    };

    return (
        <svg width="100%" height="100%" viewBox="0 0 1024 526">
            <g>
                {/* PCS1 라인 */}
                <ElecLine2 path={[ix1, iy0+ih/2, jx, iy0+ih/2, jx, iy1+ih/2]} 
                    flow={() => {
                        const p = getSafeVal(pcs1Obj, "power") ?? 0;
                        return Math.abs(p) < threshold ? 0 : p;
                    }} 
                    switch={() => opModeToSwitch(getSafeVal(pcs1Obj, "opMode")) } />
                <PcsIcon x={ix1} y={iy0} w={iw} h={ih} cvcf={() => getSafeVal(pcs1Obj, "opMode") === "islanding" }/>
                <IconLabel x={ix1} y={iy0+ih+fs} h={fs} type="ac" obj={pcs1Obj}/>
                <ElecLine2 path={[ix1+iw+70, (iy0+iy1)/2+ih/2, ix1+iw+70, iy0+ih/2, ix1+iw, iy0+ih/2 ]} 
                    flow={() => {
                        const p = getSafeVal(pcs1Obj, "power") ?? 0;
                        return Math.abs(p) < threshold ? 0 : p;
                    }} />

                {/* PCS2 라인 */}
                <ElecLine2 path={[ix1, iy1+ih/2, jx, iy1+ih/2]}
                    flow={() => opModeToFlow(getSafeVal(pcs2Obj, "opMode")) } 
                    switch={() => opModeToSwitch(getSafeVal(pcs2Obj, "opMode")) } />
                <PcsIcon x={ix1} y={iy1} w={iw} h={ih}/>
                <IconLabel x={ix1} y={iy1+ih+fs} h={fs} type="ac" obj={pcs2Obj}/>
                <ElecLine2 path={[ix1+iw+70, (iy0+iy1)/2+ih/2, ix1+iw+70, iy1+ih/2, ix1+iw, iy1+ih/2]} 
                    flow={() => opModeToFlow(getSafeVal(pcs2Obj, "opMode")) } />

                {/* Battery 라인 */}
                <ElecLine2 path={[ix2+20, (iy0+iy1)/2+ih/2, ix1+iw+70, (iy0+iy1)/2+ih/2]} 
                    flow={() => opModeToFlow(getSafeVal(batObj, "opMode")) } 
                    switch={() => opModeToSwitch(getSafeVal(batObj, "opMode")) } />
                <BatteryHorizIcon x={ix2+20} y={(iy0+iy1)/2} w={iw} h={ih}/>
                <IconLabel x={ix2} y={(iy1+iy0)/2+ih+fs} h={fs} type="bat" obj={batObj}/>

                {/* PV 라인 */}
                <ElecLine2 path={[ix1, iy2+ih/2, jx, iy2+ih/2, jx, iy1+ih/2]} 
                    flow={() => opModeToFlow(getSafeVal(pvObj, "opMode")) } 
                    switch={() => opModeToSwitch(getSafeVal(pvObj, "opMode")) } />
                <PcsIcon x={ix1} y={iy2} w={iw} h={ih}/>
                <IconLabel x={ix1} y={iy2+ih+fs} h={fs} type="ac" obj={pvObj}/>
                <ElecLine2 path={[ix2+20, iy2+ih/2, ix1+iw, iy2+ih/2]} 
                    flow={() => opModeToFlow(getSafeVal(pvObj, "opMode")) } 
                    switch={() => opModeToSwitch(getSafeVal(pvObj, "opMode")) }/>               
                <PvIcon x={ix2+20} y={iy2} w={iw} h={ih}/>
                <IconLabel x={ix2} y={iy2+ih+fs} h={fs} type="pv" obj={pvObj}/>

                {/* Meter 및 메인 전력선 */}
                <MeterIcon x={ix0} y={iy1} w={iw} h={ih}/>
                <ElecLine2 path={[ix0, iy1+ih/2, 20, iy1+ih/2]} 
                    flow={() => {
                        const cb = getSafeVal(pcs1Obj, "mainsCbOn");
                        const mPower = getSafeVal(meterObj, "power") ?? 0;
                        return cb ? -mPower : 0;
                    }} 
                    switch={() => !!getSafeVal(pcs1Obj, "mainsCbOn") } />

                {/* load와 pv/ess 사이 */}
                <ElecLine2 path={[jx, iy1+ih/2, jx-150+iw/2, iy1+ih/2]}  
                    flow={() => {
                        const pvP = getSafeVal(pvObj, "power") ?? 0;
                        const pcs1P = getSafeVal(pcs1Obj, "power") ?? 0;
                        const pcs2P = getSafeVal(pcs2Obj, "power") ?? 0;
                        return pvP + pcs1P + pcs2P;
                    }} /> 
                <ElecLine2 path={[jx-150+iw/2, iy1+ih/2, ix0+iw, iy1+ih/2]} 
                    flow={() => -(getSafeVal(meterObj, "power") ?? 0) } />
                <IconLabel x={ix0} y={iy1+ih+fs} h={fs} type="ac" obj={meterObj}/>          
                
                {/* Load */}
                <LoadIcon x={jx-150} y={iy2} w={iw} h={ih}/>
                <ElecLine2 path={[jx-150+iw/2, iy2, jx-150+iw/2, iy1+ih/2]} 
                    flow={() => -(getSafeVal(loadObj, "power") ?? 0) } />
                <IconLabel x={jx-150} y={iy2+ih+fs} h={fs} type="ac" obj={loadObj}/>
            </g>
        </svg>
    );
}
